import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Button, InputAdornment, TextField } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import LayoutOnboardingComponent from "components/layout-onboarding/LayoutOnboardingComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { login, sendForgotPasswordEmail } from "../../service/LoginService";
import { AlertComponent } from "components";
import storage from "services/storage";

const LoginPage = ({ setType, setRole }) => {
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [loginType, setLoginType] = useState("client");
  const [forgotPasswordType, setForgotPasswordType] = useState(false);

  useEffect(() => {
    if (storage.getToken()) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const dynamicLoginTextObj = useMemo(
    () => ({
      bottomAction: `Login as ${
        loginType === "admin" ? "a client" : "an administrator"
      }`,
      title:
        forgotPasswordType === "forgot"
          ? "Forgot Password"
          : forgotPasswordType === "success"
          ? "Email Sent"
          : `Login as ${
              loginType === "admin" ? "an administrator" : "a client"
            }`,
      subtitle:
        forgotPasswordType === "forgot"
          ? "Enter your email address and we will send new password"
          : forgotPasswordType === "success"
          ? "Please check your email to get new password"
          : null,
    }),
    [forgotPasswordType, loginType],
  );

  const toggleShowPassword = () => setShowPassword((show) => !show);

  const resetForgotPassword = () => {
    setErrorMessage(false);
    setSubmitted(false);
    setForgotPasswordType(false);
  };

  const toggleLoginType = () => {
    resetForgotPassword();
    setLoginType((prev) => (prev === "client" || !prev ? "admin" : "client"));
  };

  const onLogin = useCallback(
    async (event) => {
      event.preventDefault();

      setSubmitted(true);

      if (!email || !password) {
        setErrorMessage("Email and Password cannot be blank");

        return;
      }

      try {
        const payload = { email, password, type: loginType.toUpperCase() };
        const response = await login(payload);

        const { data } = response || {};

        if (data) {
          storage.setToken(data);
          storage.setItem("type", loginType.toUpperCase());
          setType(loginType.toUpperCase());

          navigate("/dashboard", { replace: true });
        } else {
          setErrorMessage("Invalid e-mail address or password");
        }
      } catch {
        setErrorMessage("Invalid e-mail address or password");
      }
    },
    [email, loginType, navigate, password, setType],
  );

  const onSubmitForgotPassword = useCallback(
    async (event) => {
      event.preventDefault();

      setSubmitted(true);

      if (!email) {
        setErrorMessage("Email cannot be blank");

        return;
      }

      try {
        const payload = { email, type: loginType.toUpperCase() };
        const response = await sendForgotPasswordEmail(payload);

        const { info } = response || {};

        if (info?.title === "success") {
          setErrorMessage(false);
          setForgotPasswordType("success");
        } else {
          const errMessage = info?.description || "Failed to reset password";
          setErrorMessage(errMessage);
        }
      } catch {
        setErrorMessage("Failed to reset password");
      }
    },
    [email, loginType],
  );

  const onEmailChange = (e) => setEmail(e.target.value);

  const onPasswordChange = (e) => setPassword(e.target.value);

  return (
    <LayoutOnboardingComponent
      title={dynamicLoginTextObj.title}
      bottomAction={
        <p
          className='cursor-pointer font-extrabold text-xs'
          onClick={toggleLoginType}
        >
          {dynamicLoginTextObj.bottomAction}
        </p>
      }
      {...(dynamicLoginTextObj.subtitle
        ? { subtitle: dynamicLoginTextObj.subtitle }
        : {})}
    >
      {Boolean(forgotPasswordType) ? (
        <div>
          {forgotPasswordType === "forgot" ? (
            <form className='grid gap-[24px]' onSubmit={onSubmitForgotPassword}>
              <div className='form-input'>
                <p className='text-[#5F737F] text-sm'>E-mail address</p>

                <TextField
                  error={submitted && !email}
                  name='email'
                  placeholder='E-mail address'
                  value={email}
                  onChange={onEmailChange}
                />
              </div>

              <div>
                <Button
                  className='h-[40px]'
                  fullWidth
                  type='submit'
                  variant='contained'
                  onClick={onSubmitForgotPassword}
                >
                  <span className='font-extrabold text-xs'> Send to Email</span>
                </Button>
                <p
                  onClick={resetForgotPassword}
                  className='cursor-pointer font-extrabold text-xs text-[#E4508F] mt-4 mb-4 text-center'
                >
                  Back to Login
                </p>
              </div>
            </form>
          ) : (
            <Button
              className='h-[40px]'
              fullWidth
              type='submit'
              variant='contained'
              onClick={resetForgotPassword}
            >
              <span className='font-extrabold text-xs'> Login</span>
            </Button>
          )}

          {Boolean(errorMessage) && (
            <AlertComponent
              icon={<ErrorIcon />}
              type='error'
              message={errorMessage}
            />
          )}
        </div>
      ) : (
        <form className='grid gap-[24px]' onSubmit={onLogin}>
          <div className='form-input'>
            <p className='text-[#5F737F] text-sm'>E-mail address</p>

            <TextField
              error={submitted && !email}
              name='email'
              placeholder='E-mail address'
              value={email}
              onChange={onEmailChange}
            />
          </div>

          <div className='form-input'>
            <p className='text-[#5F737F] text-sm'>Password</p>

            <TextField
              error={submitted && !password}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className='cursor-pointer'
                    position='end'
                    onClick={toggleShowPassword}
                  >
                    {password ? (
                      showPassword ? (
                        <Visibility />
                      ) : (
                        <VisibilityOff />
                      )
                    ) : (
                      <></>
                    )}
                  </InputAdornment>
                ),
              }}
              name='password'
              placeholder='Password'
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={onPasswordChange}
            />
            <p
              onClick={() => setForgotPasswordType("forgot")}
              className='cursor-pointer font-extrabold text-xs text-[#E4508F] mt-1'
            >
              Forgot Password
            </p>
          </div>

          <Button
            className='h-[40px]'
            fullWidth
            type='submit'
            variant='contained'
            onClick={onLogin}
          >
            <span className='font-extrabold text-xs'> Login</span>
          </Button>

          <div className='flex flex-row justify-center gap-2'>
            <p className='text-xs'>New to Qaptive?</p>
            <a href='https://sinartamagunita.co.id/pricing'>
              <p className='text-[#E4508F] text-xs font-extrabold cursor-pointer'>
                Learn More
              </p>
            </a>
          </div>

          {Boolean(errorMessage) && (
            <AlertComponent
              icon={<ErrorIcon />}
              type='error'
              message={errorMessage}
            />
          )}
        </form>
      )}
    </LayoutOnboardingComponent>
  );
};

export default LoginPage;
